import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import { Play, Pause, RotateCcw, Plus, Volume2, VolumeX, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TimerPage = () => {
  const [presets, setPresets] = useState({ default_presets: [], user_presets: [] });
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [workTime, setWorkTime] = useState(30);
  const [restTime, setRestTime] = useState(30);
  const [rounds, setRounds] = useState(10);
  const [currentRound, setCurrentRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isRunning, setIsRunning] = useState(false);
  const [isWork, setIsWork] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPreset, setNewPreset] = useState({ name: '', work_seconds: 30, rest_seconds: 30, rounds: 8 });
  
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await axios.get(`${API}/timer/presets`, { headers: getAuthHeaders() });
        setPresets(response.data);
      } catch (error) {
        console.error('Error fetching presets:', error);
      }
    };
    fetchPresets();
  }, []);

  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            playSound();
            if (isWork) {
              setIsWork(false);
              return restTime;
            } else {
              if (currentRound >= rounds) {
                setIsRunning(false);
                toast.success('Treino concluído!');
                return 0;
              }
              setCurrentRound((r) => r + 1);
              setIsWork(true);
              return workTime;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isWork, currentRound, rounds, workTime, restTime, playSound]);

  const handleStart = () => {
    if (!isRunning && currentRound === 1 && timeLeft === workTime) {
      setTimeLeft(workTime);
      setIsWork(true);
    }
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setIsRunning(false);
    setCurrentRound(1);
    setIsWork(true);
    setTimeLeft(workTime);
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setWorkTime(preset.work_seconds);
    setRestTime(preset.rest_seconds);
    setRounds(preset.rounds);
    setTimeLeft(preset.work_seconds);
    setCurrentRound(1);
    setIsWork(true);
    setIsRunning(false);
  };

  const handleSavePreset = async () => {
    if (!newPreset.name) {
      toast.error('Escreve um nome para o preset');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/timer/presets`, newPreset, { headers: getAuthHeaders() });
      setPresets((prev) => ({
        ...prev,
        user_presets: [...prev.user_presets, response.data]
      }));
      setDialogOpen(false);
      setNewPreset({ name: '', work_seconds: 30, rest_seconds: 30, rounds: 8 });
      toast.success('Preset guardado!');
    } catch (error) {
      toast.error('Erro ao guardar preset');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const totalTime = (workTime + restTime) * rounds;
  const progress = ((currentRound - 1) * (workTime + restTime) + (isWork ? workTime - timeLeft : workTime + restTime - timeLeft)) / totalTime * 100;

  return (
    <DashboardLayout>
      <div data-testid="timer-page" className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Timer de <span className="text-[#32CD32]">Intervalo</span>
          </h1>
          <p className="text-gray-400">
            Timer para treinos intervalados com presets personalizáveis
          </p>
        </div>

        {/* Hidden audio element for beep sound */}
        <audio ref={audioRef} preload="auto">
          <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQ0AADYAACxhpNnxq2tHIzKQ1fPDoW4dADEAAFB0kL7v5oeAUz15u9r6sH1mQ06Pzfr0soiEd3aKlc7/y7V8XkRWgq7n/saTe1U/VoKw7/zOnIFVQFqHtO/9yJp7UjpVgKzq/syWelU8V4Ow7fzInHxXO1aArOr9ypd7Vj5Xga7t/MqYfFk7VoCt6/3JlntYPVeBru36yJd9WD1Xgq7t+siXfVg9V4Kt7frIl31YPViBru36yJd9WD1YgK3r+smXfVg9V4Gu7PrJl31ZPVeCru36yZd9WT1Xgq3s+smXfVk9V4Kt7frJl35ZPVeCru36yZd+WT1Xgq3t+smXflk9V4Kt7frJl35ZPVeCre36yZd+WT1Xgq3t+smXflk9V4Kt7frJl35ZPVeCre36yZd+WT1Xgq3t+smXflk9V4Ku7frJl35ZPVeCre36yZd+WT1Xgq3t+smXflk9V4Ku7frJln5ZPVeBre36yZd+Wj1Wgq3t+smWflk9V4Kt7frJln5ZPVeCru36yZZ+WT1Xgq3t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1Xgq3t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1Xgq3t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1Xgq7t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1Xgq7t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1Xgq7t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1Xgq7t+smWflk9V4Ku7frJln5ZPVeCru36yZZ+WT1X" type="audio/wav" />
        </audio>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Timer Display */}
          <Card className="lg:col-span-2 bg-[#121212] border-white/10">
            <CardContent className="p-8">
              {/* Progress bar */}
              <div className="w-full h-2 bg-gray-800 rounded-full mb-8 overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-300"
                  style={{ 
                    width: `${progress}%`,
                    backgroundColor: isWork ? '#32CD32' : '#3B82F6'
                  }}
                />
              </div>

              {/* Round indicator */}
              <div className="text-center mb-4">
                <span className="text-xs font-mono tracking-widest text-gray-500 uppercase">
                  Round {currentRound} / {rounds}
                </span>
              </div>

              {/* Phase indicator */}
              <div className="text-center mb-4">
                <span 
                  className={`text-2xl font-bold uppercase tracking-wider ${isWork ? 'text-[#32CD32]' : 'text-blue-500'}`}
                  style={{ fontFamily: 'Barlow Condensed' }}
                >
                  {isWork ? 'TRABALHO' : 'DESCANSO'}
                </span>
              </div>

              {/* Time display */}
              <div className="text-center mb-8">
                <span 
                  className={`timer-display ${isWork ? 'timer-work' : 'timer-rest'}`}
                  data-testid="timer-display"
                >
                  {formatTime(timeLeft)}
                </span>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="w-14 h-14 rounded-full border-white/20"
                  onClick={handleReset}
                  data-testid="timer-reset-btn"
                >
                  <RotateCcw className="w-6 h-6" />
                </Button>
                
                <Button
                  className={`w-20 h-20 rounded-full text-xl font-bold ${isRunning ? 'bg-red-500 hover:bg-red-600' : 'btn-primary'}`}
                  onClick={handleStart}
                  data-testid="timer-start-btn"
                >
                  {isRunning ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  className={`w-14 h-14 rounded-full border-white/20 ${soundEnabled ? 'text-[#32CD32]' : 'text-gray-500'}`}
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  data-testid="timer-sound-btn"
                >
                  {soundEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
                </Button>
              </div>

              {/* Settings display */}
              <div className="flex justify-center gap-8 mt-8 text-sm text-gray-500">
                <div className="text-center">
                  <p className="font-mono text-lg text-white">{workTime}s</p>
                  <p>Trabalho</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-lg text-white">{restTime}s</p>
                  <p>Descanso</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-lg text-white">{formatTime(totalTime)}</p>
                  <p>Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Presets */}
          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Presets
                </CardTitle>
                <CardDescription>Configurações prontas</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="icon" variant="ghost" className="text-[#32CD32]" data-testid="add-preset-btn">
                    <Plus className="w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10">
                  <DialogHeader>
                    <DialogTitle>Novo Preset</DialogTitle>
                    <DialogDescription>Cria uma configuração personalizada</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        value={newPreset.name}
                        onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })}
                        placeholder="O Meu Treino"
                        className="bg-black/50 border-white/20"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Trabalho (s)</Label>
                        <Input
                          type="number"
                          value={newPreset.work_seconds}
                          onChange={(e) => setNewPreset({ ...newPreset, work_seconds: parseInt(e.target.value) || 0 })}
                          className="bg-black/50 border-white/20"
                        />
                      </div>
                      <div>
                        <Label>Descanso (s)</Label>
                        <Input
                          type="number"
                          value={newPreset.rest_seconds}
                          onChange={(e) => setNewPreset({ ...newPreset, rest_seconds: parseInt(e.target.value) || 0 })}
                          className="bg-black/50 border-white/20"
                        />
                      </div>
                      <div>
                        <Label>Rounds</Label>
                        <Input
                          type="number"
                          value={newPreset.rounds}
                          onChange={(e) => setNewPreset({ ...newPreset, rounds: parseInt(e.target.value) || 0 })}
                          className="bg-black/50 border-white/20"
                        />
                      </div>
                    </div>
                    <Button className="w-full btn-primary" onClick={handleSavePreset}>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Preset
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
              {[...presets.default_presets, ...presets.user_presets].map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handlePresetSelect(preset)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    selectedPreset?.name === preset.name
                      ? 'bg-[#32CD32]/10 border border-[#32CD32]/50'
                      : 'bg-black/30 border border-white/5 hover:border-[#32CD32]/30'
                  }`}
                  data-testid={`preset-${preset.name}`}
                >
                  <p className="font-medium text-sm">{preset.name}</p>
                  <p className="text-xs text-gray-500">
                    {preset.work_seconds}s / {preset.rest_seconds}s × {preset.rounds}
                  </p>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TimerPage;
